import ContractingFooter from "features/contracting/components/ContractingFooter";
import ContractingStepFormProps from "features/contracting/models/ContractingStepFormProps";
import { useContractingStep } from "features/contracting/hooks/useContractingStep";
import PageCard from "features/shared/components/card/PageCard";
import { useForm } from "features/shared/forms/hooks/useForm";
import IAppState from "infrastructure/store/IAppState";
import ContractingState from "infrastructure/store/contracting/ContractingState";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import CustomerContactDataSubForm from "features/contracting/components/customerData/CustomerContactDataSubForm";
import {
  customerDataFormValidations,
  setBusinessPersonValidations,
} from "./customerDataFormValidation";
import {
  authorizedAgentKey,
  contactPersonKey,
  correspondenceAddressKey,
  getFormInitValues,
  hasContactPerson,
  hasCorrespondenceAddress,
  isBusinessType,
  mainAddressKey,
} from "./customerDataFormHelper";
import AddressSubForm from "features/contracting/components/address/AddressSubForm";
import RenderWrapper from "features/shared/components/renderWrapper/RenderWrapper";
import RadioBoxBoolSubForm from "features/shared/forms/components/radioBox/RadioBoxBoolSubForm";
import { setAddressValidations } from "features/contracting/components/address/addressSubFormValidation";
import { useContractingCustomerTypeValue } from "features/contracting/hooks/useContractingCustomerTypeValue";
import { useContractingAddressName } from "features/contracting/hooks/useContractingAddressName";
import MailMethodSubForm from "features/contracting/components/customerData/MailMethodSubForm";
import { UserEnumDTO } from "features/shared/enums/dynamic/UserEnumDTO";
import DegreeDTO from "features/shared/dtos/DegreeDTO";
import HouseholdDataSubForm from "features/contracting/components/customerData/HouseholdDataSubForm";
import BusinessDataSubForm from "features/contracting/components/customerData/BusinessDataSubForm";
import BusinessPersonSubForm from "features/contracting/components/customerData/BusinessPersonSubForm";
import { CustomerType } from "features/customer/enums/customerType";
import { useTranslationWithPrefix } from "features/shared/hooks/useTranslationWithPrefix";

const CustomerDataFormSection = (props: ContractingStepFormProps) => {
  // translations
  const tLabels = useTranslationWithPrefix("contracting\\contracting", "customerDataFormSection");

  // store
  const stepData = useSelector<IAppState, ContractingState>(
    (store) => store.contracting
  ).progress.steps[props.actualStep.component];

  const configuration = useSelector<IAppState, ContractingState>(
    (store) => store.contracting
  ).configuration;

  const customerType =
    useContractingCustomerTypeValue<CustomerType>("customerType");

  const addressName = useContractingAddressName();
  const mailMethods: UserEnumDTO[] = configuration.mailMethodTypes!;
  const degrees: DegreeDTO[] = configuration.degrees!;

  // refs
  const phonePrefixEl = useRef<HTMLSelectElement>(null);
  const authorizedAgentphonePrefixEl = useRef<HTMLSelectElement>(null);
  const contactPersonPhonePrefixEl = useRef<HTMLSelectElement>(null);

  // form hook
  const formHandler = useForm({
    initialValues: getFormInitValues(stepData.values, customerType),
    initialValidations: customerDataFormValidations(
      customerType,
      phonePrefixEl,
      authorizedAgentphonePrefixEl
    ),
  });

  const {
    values,
    errors,
    bindFieldNew,
    valideteForm,
    getFormValue,
    clearFormValues,
    setValidationRule,
  } = {
    ...formHandler,
  };

  // form-step hook
  const { onNextClick, onPrevClick } = useContractingStep({
    ...props,
    formValues: values,
    validateForm: valideteForm,
  });

  // side-effects
  // handling validation/clearing of custom correspondence address
  useEffect(() => {
    if (hasCorrespondenceAddress(getFormValue)) {
      setAddressValidations(correspondenceAddressKey, setValidationRule);
    } else {
      setAddressValidations(
        correspondenceAddressKey,
        setValidationRule,
        "remove"
      );
      clearFormValues([correspondenceAddressKey]);
    }
  }, [getFormValue("correspondenceAddressIsSameAsMain")]);

  // handling validations of contact person
  useEffect(() => {
    if (hasContactPerson(getFormValue)) {
      setBusinessPersonValidations(
        contactPersonKey,
        contactPersonPhonePrefixEl,
        setValidationRule
      );
    } else {
      setBusinessPersonValidations(
        contactPersonKey,
        contactPersonPhonePrefixEl,
        setValidationRule,
        "remove"
      );
    }
  }, [getFormValue("contactPersonIsSameAsAuthorizedAgent")]);

  // sub-renders
  const renderCustomerDataForm = () => {
    switch (customerType) {
      case "Person":
        return (
          <HouseholdDataSubForm
            formHandler={formHandler}
            degrees={degrees}
            disabled={stepData.disabled}
          />
        );
      case "Company":
        return (
          <BusinessDataSubForm
            formHandler={formHandler}
            enableAresAutocomplete={configuration.enableAresAutocomplete}
            addressObjectPrefix={mainAddressKey}
            disabled={stepData.disabled}
          />
        );
      default:
        return <></>;
    }
  };

  const renderBussinesPersonForms = () => {
    switch (customerType) {
      case "Company":
        return (
          <>
          <PageCard
            title={tLabels("bussinesPersonCardTitle")}
            bodyClassName="pt-4 px-4"
            cardClassName="mb-4"
          >
            <BusinessPersonSubForm
              formHandler={formHandler}
              objectPrefix={authorizedAgentKey}
              degrees={configuration.degrees!}
              mobilePhonePrefixRef={authorizedAgentphonePrefixEl}
              disabled={stepData.disabled}
              enableMobileTooltip
            />
          </PageCard>

          <PageCard
            title={tLabels("contactPersonCardTitle")}
            bodyClassName="pt-4 px-4"
            cardClassName="mb-4"
          >
            <div className="row mx-n2">
              <div className="col-md-6 px-2">
                <RadioBoxBoolSubForm
                  name={"contactPersonIsSameAsAuthorizedAgent"}
                  label={tLabels("contactPersonIsSameAsAuthorizedAgent")}
                  formHandler={formHandler}
                  trueBox={{disabled: stepData.disabled}}
                  falseBox={{disabled: stepData.disabled}}
                />
              </div>
            </div>
            <RenderWrapper isVisible={() => hasContactPerson(getFormValue)}>
              <BusinessPersonSubForm
                formHandler={formHandler}
                objectPrefix={contactPersonKey}
                degrees={configuration.degrees!}
                mobilePhonePrefixRef={contactPersonPhonePrefixEl}
                disabled={stepData.disabled}
              />
            </RenderWrapper>
          </PageCard>
        </>
        );
      default:
        return <></>;
    }
  };

  // render
  return (
    <div>
      {renderCustomerDataForm()}
      <CustomerContactDataSubForm
        bindField={bindFieldNew}
        mobilePhonePrefixRef={phonePrefixEl}
        errors={errors}
        showPhoneInpunt={isBusinessType(customerType)}
        disabled={stepData.disabled}
        enableMobileTooltip={customerType === "Person"}
      />
      {renderBussinesPersonForms()}
      <PageCard
        title={addressName}
        bodyClassName="pt-4 px-4"
        cardClassName="mb-4"
      >
        <AddressSubForm
          formHandler={formHandler}
          enableAutocomplete={configuration.enableAddressAutocomplete}
          objectPrefix={mainAddressKey}
          disabled={stepData.disabled}
        />
      </PageCard>
      <PageCard
        title={tLabels("correspondenceAddressCardTitle")}
        bodyClassName="pt-4 px-4"
        cardClassName="mb-4"
      >
        <div className="row mx-n2">
          <div className="col-md-6 px-2">
            <RadioBoxBoolSubForm
              name={"correspondenceAddressIsSameAsMain"}
              label={`${tLabels("correspondenceAddressIsSameAsMainPrefix")} ${addressName.toLocaleLowerCase()}?`}
              formHandler={formHandler}
              trueBox={{disabled: stepData.disabled}}
              falseBox={{disabled: stepData.disabled}}
            />
          </div>
        </div>
        <RenderWrapper isVisible={() => hasCorrespondenceAddress(getFormValue)}>
          <AddressSubForm
            formHandler={formHandler}
            enableAutocomplete={configuration.enableAddressAutocomplete}
            objectPrefix={correspondenceAddressKey}
            disabled={stepData.disabled}
          />
        </RenderWrapper>
      </PageCard>
      <MailMethodSubForm 
        mailMethods={mailMethods} 
        formHandler={formHandler} 
        disabled={stepData.disabled} 
      />
      <ContractingFooter onNextClick={onNextClick} onPrevClick={onPrevClick} />
    </div>
  );
};

export default CustomerDataFormSection;
